import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useCases, useConnectors, useFlows, useTemplates } from "@/api/queries";
import type { ConnectorSummary } from "@/api/types";
import { useTheme } from "@/app/theme-context";
import { Glyph } from "@/components/ui/Glyph";
import { AppTile } from "@/components/ui/Tile";
import type { GlyphName } from "@/lib/glyphs";
import { BOTTOM_NAV, MAIN_NAV } from "./nav";
import { GO_KEYS, useSingleKeys } from "./shortcuts";
import { useModal } from "./useModal";
import styles from "./palette.module.css";

type Group =
  | "From what you typed"
  | "Go to"
  | "Actions"
  | "Cases"
  | "Flows"
  | "Templates"
  | "Connect a system";

interface Entry {
  id: string;
  group: Group;
  label: string;
  /** Quiet text at the right: a key, a state, a role. */
  hint?: string;
  /** Keys that do the same, shown at the right instead of a hint. */
  keys?: string[];
  /** Extra words the entry answers to. */
  words?: string;
  glyph?: GlyphName;
  /** A connected system's key: its brand logo is drawn instead of a glyph. */
  app?: string;
  to?: string;
  run?: () => void;
}

/** "Create a flow restart the app pool when it hangs": the words after the command. */
const CREATE = /^(?:create|new|draft|make|build)\s+(?:a\s+)?flows?\b[\s:,-]*(.*)$/i;

const STATE_HINT: Record<ConnectorSummary["state"], string> = {
  connected: "Connected",
  attention: "Needs attention",
  untested: "Not tested yet",
  available: "Not set up",
};

/** Every word typed appears somewhere in the text. */
function matches(text: string, query: string): boolean {
  const hay = text.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => hay.includes(word));
}

/** How closely an entry answers the words typed: its name starts with them, a word in it does, or they appear somewhere. */
function score(entry: Entry, query: string): number {
  const q = query.toLowerCase();
  const name = entry.label.replace(/^(Connect|Open template) /, "").toLowerCase();
  if (name.startsWith(q)) return 3;
  if (name.split(/[^a-z0-9]+/).some((w) => w.startsWith(q.split(/\s+/)[0] ?? q))) return 2;
  return 1;
}

/** The builder, drawing straight away when the person already said what the flow should do. */
const builderPath = (prompt: string) =>
  prompt ? `/flows/new?prompt=${encodeURIComponent(prompt)}&go=1` : "/flows/new";

/** Ctrl K: jump to a screen, a case, a flow or a template, connect a system, or ask the crew. */
export function CommandPalette({
  initialQuery = "",
  onClose,
  onShortcuts,
}: {
  initialQuery?: string;
  onClose: () => void;
  /** Opens the keyboard shortcuts overlay. */
  onShortcuts: () => void;
}) {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const singleKeys = useSingleKeys();
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useModal(box, onClose, input);
  const cases = useCases();
  const flows = useFlows();
  const connectors = useConnectors();
  const templates = useTemplates();

  const entries = useMemo<Entry[]>(() => {
    const goKey = (to: string) => {
      const g = GO_KEYS.find((k) => k.to === to);
      return singleKeys && g ? ["G", g.key.toUpperCase()] : undefined;
    };
    const nav: Entry[] = [
      ...[...MAIN_NAV, ...BOTTOM_NAV].map((d) => ({
        id: `nav:${d.to}`,
        group: "Go to" as const,
        label: d.label,
        glyph: d.glyph,
        to: d.to,
        keys: goKey(d.to),
      })),
      {
        id: "nav:templates",
        group: "Go to",
        label: "Templates",
        glyph: "doc",
        to: "/templates",
        keys: goKey("/templates"),
      },
      {
        id: "nav:autonomy",
        group: "Go to",
        label: "Autonomy and guardrails",
        glyph: "shield",
        to: "/autonomy",
      },
      {
        id: "nav:war-room",
        group: "Go to",
        label: "War room",
        glyph: "flag",
        to: "/war-rooms/current",
      },
    ];
    const actions: Entry[] = [
      {
        id: "do:create",
        group: "Actions",
        label: "Create a flow from plain English",
        words: "new flow describe a fix draft build",
        glyph: "sparkle",
        to: "/flows/new",
      },
      {
        id: "do:templates",
        group: "Actions",
        label: "Browse templates",
        words: "start from a template examples",
        glyph: "doc",
        to: "/templates",
      },
      {
        id: "do:theme",
        group: "Actions",
        label: theme === "light" ? "Switch to dark mode" : "Switch to light mode",
        words: "theme appearance light dark",
        glyph: theme === "light" ? "moon" : "sun",
        keys: singleKeys ? ["T"] : undefined,
        run: toggle,
      },
      {
        id: "do:keys",
        group: "Actions",
        label: "Keyboard shortcuts",
        words: "keys help",
        glyph: "lines",
        keys: singleKeys ? ["?"] : undefined,
        run: onShortcuts,
      },
    ];
    const q = query.trim();
    if (!q) return [...actions.slice(0, 2), ...nav, ...actions.slice(2)];

    const caseEntries: Entry[] = (cases.data?.cases ?? []).map((c) => ({
      id: `case:${String(c.number)}`,
      group: "Cases",
      label: `${String(c.number)} ${c.title}`,
      hint: c.stage_text,
      words: c.ci ?? "",
      glyph: "cases",
      to: `/cases/${String(c.number)}`,
    }));
    const flowEntries: Entry[] = (flows.data?.flows ?? []).map((f) => ({
      id: `flow:${f.key}`,
      group: "Flows",
      label: f.description,
      hint: f.key,
      glyph: "flows",
      to: `/flows/${f.key}`,
    }));
    const templateEntries: Entry[] = (templates.data?.templates ?? []).map((t) => ({
      id: `template:${t.key}`,
      group: "Templates",
      label: `Open template ${t.name}`,
      hint: t.role_label,
      words: `${t.summary} ${t.connectors.join(" ")}`,
      app: t.connectors[0],
      glyph: "doc",
      to: `/templates/${t.key}`,
    }));
    const connectEntries: Entry[] = (connectors.data?.connectors ?? []).map((c) => ({
      id: `connect:${c.key}`,
      group: "Connect a system",
      label: `Connect ${c.name}`,
      hint: STATE_HINT[c.state],
      words: `${c.key} ${c.category} ${c.tags.join(" ")}`,
      app: c.key,
      to: `/connections?c=${encodeURIComponent(c.key)}`,
    }));

    const create = CREATE.exec(q);
    const prompt = create ? (create[1] ?? "").trim() : "";
    const first: Entry[] = create
      ? [
          {
            id: "do:create-prompt",
            group: "From what you typed",
            label: "Create a flow from plain English",
            hint: prompt ? `“${prompt}”` : undefined,
            glyph: "sparkle",
            to: builderPath(prompt),
          },
        ]
      : [];
    // A case number opens that case directly, even before the list has loaded or when it's closed.
    const byNumber: Entry[] =
      /^\d{3,7}$/.test(q) && !caseEntries.some((e) => e.id === `case:${q}`)
        ? [
            {
              id: `case:${q}`,
              group: "Cases",
              label: `Case ${q}`,
              glyph: "cases",
              to: `/cases/${q}`,
            },
          ]
        : [];
    const found = (items: Entry[], limit: number) =>
      items
        .filter((e) => matches(`${e.label} ${e.hint ?? ""} ${e.words ?? ""}`, q))
        .map((e) => ({ e, score: score(e, q) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);
    // Each kind of result stays together; the kind with the closest match comes first.
    const kinds = [
      found(nav, 4),
      found(actions, 4).filter(({ e }) => !(create && e.id === "do:create")),
      [...byNumber.map((e) => ({ e, score: 4 })), ...found(caseEntries, 5)],
      found(flowEntries, 5),
      found(templateEntries, 4),
      found(connectEntries, 6),
    ].filter((k) => k.length > 0);
    const best = (k: { score: number }[]) => Math.max(...k.map((r) => r.score));
    const results = kinds
      .map((k, order) => ({ k, order, best: best(k) }))
      .sort((a, b) => b.best - a.best || a.order - b.order)
      .flatMap(({ k }) => k.map(({ e }) => e));
    const typed: Entry[] = [
      ...(create
        ? []
        : [
            {
              id: "do:create-typed",
              group: "From what you typed" as const,
              label: "Create a flow from plain English",
              hint: `“${q}”`,
              glyph: "sparkle" as const,
              to: builderPath(q),
            },
          ]),
      {
        id: "ask",
        group: "From what you typed",
        label: "Ask the crew",
        hint: `“${q}”`,
        glyph: "chat",
        to: `/ask?q=${encodeURIComponent(q)}`,
      },
    ];
    // A typed command comes first; plain words end with what to do with them.
    return [...first, ...results, ...typed];
  }, [
    cases.data,
    flows.data,
    templates.data,
    connectors.data,
    query,
    theme,
    toggle,
    onShortcuts,
    singleKeys,
  ]);

  const shown = Math.min(active, Math.max(0, entries.length - 1));

  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>(`#zh-pal-${String(shown)}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [shown]);

  const go = (entry: Entry | undefined) => {
    if (!entry) return;
    onClose();
    if (entry.run) entry.run();
    else if (entry.to) void navigate(entry.to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(shown + 1, entries.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(shown - 1, 0));
    } else if (e.key === "Home" && e.ctrlKey) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End" && e.ctrlKey) {
      e.preventDefault();
      setActive(entries.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(entries[shown]);
    }
  };

  // Consecutive entries of one group sit under its name.
  const groups: { title: Group; items: { entry: Entry; index: number }[] }[] = [];
  entries.forEach((entry, index) => {
    const last = groups[groups.length - 1];
    if (last && last.title === entry.group) last.items.push({ entry, index });
    else groups.push({ title: entry.group, items: [{ entry, index }] });
  });

  return createPortal(
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={`zh-glass ${styles.panel}`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className={styles.field}>
          <Glyph name="search" size={17} />
          <input
            ref={input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Search, or describe a flow to create"
            aria-label="Search cases, flows, templates and systems, or type a command"
            aria-controls="zh-palette-list"
            aria-activedescendant={entries[shown] ? `zh-pal-${String(shown)}` : undefined}
            aria-autocomplete="list"
            role="combobox"
            aria-expanded="true"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className={styles.esc}>Esc</kbd>
        </div>
        <div
          ref={list}
          id="zh-palette-list"
          role="listbox"
          aria-label="Results"
          className={styles.list}
        >
          {groups.map((g, gi) => (
            <div
              key={`${g.title}-${String(gi)}`}
              role="group"
              aria-labelledby={`zh-pal-g${String(gi)}`}
            >
              <div id={`zh-pal-g${String(gi)}`} role="presentation" className={styles.group}>
                {g.title}
              </div>
              {g.items.map(({ entry, index }) => (
                <Option
                  key={entry.id}
                  entry={entry}
                  index={index}
                  selected={index === shown}
                  onHover={() => setActive(index)}
                  onPick={() => go(entry)}
                />
              ))}
            </div>
          ))}
        </div>
        <div className={styles.foot} aria-hidden="true">
          <span>
            <kbd className={styles.key}>↑</kbd>
            <kbd className={styles.key}>↓</kbd> to move
          </span>
          <span>
            <kbd className={styles.key}>Enter</kbd> to open
          </span>
          <span className={styles.footTip}>
            Type “create a flow” and what it should do to start one
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Option({
  entry,
  index,
  selected,
  onHover,
  onPick,
}: {
  entry: Entry;
  index: number;
  selected: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  let icon: ReactNode;
  if (entry.app) {
    icon = <AppTile app={entry.app} size={30} radius={9} border={1} />;
  } else {
    icon = (
      <span className={`${styles.icon} ${entry.glyph === "sparkle" ? styles.iconModel : ""}`}>
        <Glyph
          name={entry.glyph ?? "search"}
          size={15}
          strokeWidth={entry.glyph === "sparkle" ? 2.2 : 2}
        />
      </span>
    );
  }
  return (
    <div
      id={`zh-pal-${String(index)}`}
      role="option"
      aria-selected={selected}
      className={styles.option}
      onMouseMove={selected ? undefined : onHover}
      onClick={onPick}
    >
      {icon}
      <span className={styles.label}>{entry.label}</span>
      {entry.keys ? (
        <span className={styles.keys} aria-hidden="true">
          {entry.keys.map((k) => (
            <kbd key={k} className={styles.key}>
              {k}
            </kbd>
          ))}
        </span>
      ) : entry.hint ? (
        <span className={styles.hint}>{entry.hint}</span>
      ) : null}
    </div>
  );
}
